import {
  Directive,
  effect,
  ElementRef,
  HostListener,
  inject,
  Input,
  InjectionToken,
} from '@angular/core';
import { AbstractControl, FormGroupDirective } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

interface VerifyEmailFacade {
  verifyEmail(email: string): void;
  alreadyVerify(): Record<string, { IsValid?: boolean }>;
}

export const VERIFY_EMAIL_FACADE = new InjectionToken<VerifyEmailFacade>('VERIFY_EMAIL_FACADE');

@Directive({
  selector: '[verify-email]',
  standalone: true,
})
export class VerifyEmailDirective {
  private readonly verifyEmailFacade = inject(VERIFY_EMAIL_FACADE, { optional: true });
  private elementRef = inject(ElementRef);
  private translateService = inject(TranslateService);

  private rootFormGroup = inject(FormGroupDirective);
  private control: AbstractControl;
  @Input() set formControlName(value: string) {
    this.control = this.rootFormGroup?.form?.controls?.[value];
  }

  private emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

  private value: string;

  private observeVerifyEmail = effect(() => {
    const alreadyVerify = this.verifyEmailFacade?.alreadyVerify?.();
    if (!alreadyVerify) return;

    if (Object.prototype.hasOwnProperty.call(alreadyVerify, this.value) && !alreadyVerify[this.value]?.IsValid) {
      this.removeElementError();
      this.addElementError();
    }
  });

  @HostListener('blur', ['$event'])
  public onBlur(e: FocusEvent) {
    this.removeElementError();

    this.value = (e.target as HTMLInputElement).value;
    const facade = this.verifyEmailFacade;
    if (!facade) return;

    const alreadyVerify = facade.alreadyVerify?.() ?? {};
    const hasEntry = Object.prototype.hasOwnProperty.call(alreadyVerify, this.value);

    if (this.value && (this.control ? this.control.valid : true) && this.emailRegex.test(this.value) && !hasEntry) {
      facade.verifyEmail?.(this.value);
    } else if (hasEntry && !alreadyVerify?.[this.value]?.IsValid) {
      this.removeElementError();
      this.addElementError();
    }
  }

  private removeElementError(): void {
    const nextElement = this.elementRef.nativeElement.nextSibling as HTMLElement;
    if (nextElement?.id === 'email-error') nextElement.remove();
  }

  private addElementError(): void {
    const div = document.createElement('div');
    div.setAttribute('id', 'email-error');
    div.classList.add('absolute', 'min-h-5', 'flex', 'gap-1', 'text-sm', 'text-[#bc3803]', 'dark:text-[#ffcc85]');

    const i = document.createElement('i');
    i.classList.add('fi', 'fi-rr-exclamation', 'mt-0.5');
    div.appendChild(i);

    const span = document.createElement('span');
    span.textContent = this.translateService.instant('FORMS.INVALID_OR_NON_EXISTENT_EMAIL');
    div.appendChild(span);

    this.elementRef.nativeElement.insertAdjacentElement('afterend', div);
  }
}
